/**
 * PanelShowcase — antd 패널 계열 컴포넌트(Collapse/Tabs/Splitter/Steps 등) 카탈로그.
 * (상세 JSDoc 헤더는 본 파일 하단 export default 함수 위 참조)
 */
import { useState } from 'react'
import {
  Card,
  Collapse,
  Divider,
  Tabs,
  Splitter,
  Steps,
  Space,
  Button,
  Typography,
  Watermark,
  Anchor
} from 'antd'
import {
  HomeOutlined,
  SettingOutlined,
  BarChartOutlined,
  PlusOutlined,
  EditOutlined,
  DownloadOutlined,
  DeleteOutlined
} from '@ant-design/icons'
import PageContainer from '@/components/common/PageContainer'
import { useLabel } from '@/framework/useLabel'

// ─── Typography 서브 컴포넌트 구조분해 ─
// antd Typography 는 Title/Text/Paragraph/Link 등 명시적 서브 컴포넌트로 의미 분리
// (h1~h6 + p + span + a 의 antd 테마 적용 버전)
// import 상단에 한 번만 destructure 해두면 JSX 가 깔끔
const { Paragraph, Title, Text, Link } = Typography

// ─── 카드 body 공통 스타일 ─
// height: calc(100% - 38px) — Card 헤더 38px 제외 가용 영역
// overflow: auto — 셀 단위 스크롤 (그리드 레이아웃 흐트러지지 않게)
//
// 【왜 module-scope 상수?】
//   - 컴포넌트 내부 객체 리터럴이면 매 렌더마다 새 reference 생성 → Card 내부 React.memo 무력화
//   - module-scope 로 두면 동일 reference 공유 → 자식 re-render 최소화
// 【주의】 height 값을 바꿀 땐 antd Card 헤더 default(38px) 기준이 맞는지 확인
//          (size='small' 도 38px, size='default' 는 56px → 그 경우 calc(100% - 56px))
// 【확장 시】
//   카드 body 에 padding 0 이 필요한 경우(Tabs full-bleed 등)는
//   별도 cardBodyStyleNoPad 를 만들어 카드별로 분기 (조건부 styling 회피)
//   현재는 모든 카드가 동일 padding 10 → 단일 상수로 충분
const cardBodyStyle = { padding: 10, height: 'calc(100% - 38px)', overflow: 'auto' as const }

/**
 * ============================================================================
 * PanelShowcase — 패널/레이아웃 분할 카탈로그
 * ============================================================================
 *
 * 【역할】 화면을 영역으로 나누거나 접고/펴는 컴포넌트 군 시연.
 *         antd Collapse/Tabs/Splitter/Steps/Card 변형 + Toolbar/Divider/Anchor
 *         + Typography 풀세트 + Watermark.
 *
 * 【시연 항목】
 *   - Collapse — 3 패널 + defaultActiveKey
 *   - Tabs — 아이콘 라벨, 3개 탭
 *   - Splitter — 좌(40%) / 우(60%) 가변 분할
 *   - Steps — vertical, current 제어 + 이전/다음 버튼
 *   - Card 변형 — type='inner' / cover 이미지 카드
 *   - Toolbar/Divider — 좌우 액션 버튼 + Divider 텍스트
 *   - Typography — Text 변형 + Paragraph(ellipsis/copyable)
 *   - Watermark — 워터마크 + 스크롤 영역
 *
 * 【사용처】 router/index.tsx — `{ path: '/showcase/panel' }` (lazy)
 * 【포팅 원본】 vue-spring-fw `ShowcasePanel.vue` 의 React/antd 포팅
 * 【의존성】 antd Collapse/Tabs/Splitter/Steps/Watermark/Anchor/Divider/Typography
 * 【참고】 https://ant.design/components/tabs
 */
// ─────────────────────────────────────────────────────────
// 컴포넌트 본체
// ─────────────────────────────────────────────────────────
// PageContainer 안에 8 셀 그리드 — 각 셀이 독립 카드.
// 각 카드의 인터랙션 상태(current) 외에는 별도 state 없음.
// 페이지 진입 시점에 모든 카드가 동시 마운트되며, 카드 안 위젯들은 각자 uncontrolled 모드로 동작.
// (예시 카탈로그라 대부분 외부 상태 동기화 불필요)
export default function PanelShowcase() {
  const { t } = useLabel()
  // ─── Steps current 인덱스 (0~2) — 이전/다음 버튼으로 이동 ─
  // controlled 모드 — onChange 콜백으로 setCurrent 호출 시 Steps 가 즉시 갱신
  // 외부 버튼(이전/다음)에서도 동일 setter 사용 → 양방향 일관성
  const [current, setCurrent] = useState(0)

  // ─── 렌더 ─
  // PageContainer: overflow=hidden 으로 페이지 자체 스크롤 차단
  //   → 카드 내부 overflow=auto 가 단독 작동 (이중 스크롤 방지)
  // 헤더(Title) + 그리드(8 cells) 의 2단 flex 레이아웃
  //   - 헤더: flex='0 0 auto' (고정 높이)
  //   - 그리드: flex=1 (잔여 공간 모두 차지)
  return (
    <PageContainer style={{ overflow: 'hidden' }}>
      <div style={{ flex: '0 0 auto', marginBottom: 8 }}>
        <Title level={4} style={{ margin: 0 }}>
          {t('LBL_PANEL_COMPONENTS', 'Panel 레이아웃 컴포넌트')}
        </Title>
      </div>

      {/* ─── Grid: 4 col × 2 row 패널 카탈로그 ─
          row1: Collapse / Tabs / Splitter / Steps
          row2: Card 변형 / Toolbar+Divider / Typography / Watermark

          【왜 CSS Grid?】
            - 8개 셀 동일 비율 분배 → flex 보다 선언적
            - minHeight: 0 + 1fr 로 컨테이너 풀 높이를 정확히 양분 (셀 내부 overflow 제어 가능)
          【주의】 gridTemplateRows: '1fr 1fr' 가 동작하려면 부모가 명시적 height (flex:1) 필수
                  안 그러면 콘텐츠 고유 높이로 수축 → 카드들이 작아짐 */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gridTemplateRows: '1fr 1fr',
          gap: 10,
          overflow: 'hidden'
        }}
      >
        {/* ─── [Cell 1] Collapse — defaultActiveKey=['1'] 첫 패널 펼침 ─
            items[].label = 패널 헤더 / .children = 본문

            【언제 Collapse 를 쓸까?】
              - 정보 우선순위가 명확히 분리될 때 (필수 정보 펼침 + 보조는 접기)
              - 폼이 길어 사용자가 한 번에 보기 부담스러울 때 (단계별 그룹)
              【반대】 동일 우선순위 병렬 콘텐츠 = Tabs, 영구 표시 = Card 분리
            【주의】 controlled (activeKey + onChange) 로 쓰면 부모 state 동기화 필수 */}
        <Card
          size="small"
          title="Collapse"
          style={{ height: '100%' }}
          styles={{ body: cardBodyStyle }}
        >
          {/* size='small' — 카탈로그 셀 폭이 좁아 작은 사이즈로 밀도 확보
              defaultActiveKey 는 string[] — 다중 활성 모드에서 여러 키 동시 펼침 가능
              items[].key 는 React 재배치 안정성 + Collapse 내부 활성 추적용 PK */}
          <Collapse
            size="small"
            defaultActiveKey={['1']}
            items={[
              // key='1' — 첫 패널을 기본 펼침 상태로
              {
                key: '1',
                label: t('TTL_STEP_BASIC', '기본 정보'),
                children: <p style={{ margin: 0 }}>{t('LBL_PANEL_1_BODY', '패널 1 내용')}</p>
              },
              // key='2','3' — 접힌 상태로 시작, 사용자 클릭 시 펼침
              {
                key: '2',
                label: t('LBL_DEMO_INFO_DETAIL', '상세 정보'),
                children: <p style={{ margin: 0 }}>{t('LBL_PANEL_2_BODY', '패널 2 내용')}</p>
              },
              {
                key: '3',
                label: t('LBL_EXTRA_SETTINGS', '추가 설정'),
                children: <p style={{ margin: 0 }}>{t('LBL_PANEL_3_BODY', '패널 3 내용')}</p>
              }
            ]}
          />
        </Card>

        {/* ─── [Cell 2] Tabs — 아이콘 + 라벨 헤더 ─
            HomeOutlined/SettingOutlined/BarChartOutlined 아이콘 옆 텍스트

            【언제 Tabs vs Collapse?】
              - Tabs: 한 번에 한 패널만 보여도 OK + 수평 공간 충분
              - Collapse: 다중 동시 표시 가능 + 수직 스크롤 환경
            【왜 아이콘 + 텍스트?】
              - 아이콘만: 비주얼 가벼움 but 의미 추측 어려움 (학습 비용)
              - 텍스트만: 명확 but 단조로움 / 다국어 시 폭 가변
              - 조합: 빠른 인지(아이콘) + 정확성(텍스트) 모두 확보
            【주의】 defaultActiveKey 는 마운트 시점만 적용 — 동적 변경은 activeKey(controlled) */}
        <Card size="small" title="Tabs" style={{ height: '100%' }} styles={{ body: cardBodyStyle }}>
          {/* defaultActiveKey='1' — 첫 탭 기본 활성
              uncontrolled 모드 — 외부 상태 동기화 불필요 (단순 시연) */}
          <Tabs
            size="small"
            defaultActiveKey="1"
            items={[
              // 탭 1: Home 아이콘 + 기본
              {
                key: '1',
                label: (
                  <span>
                    <HomeOutlined /> {t('LBL_DEMO_BASIC', '기본')}
                  </span>
                ),
                children: <p style={{ margin: 0 }}>{t('LBL_TAB_BASIC_BODY', '기본 정보 탭.')}</p>
              },
              // 탭 2: Setting 아이콘 + 설정
              {
                key: '2',
                label: (
                  <span>
                    <SettingOutlined /> {t('LBL_DEMO_SETTINGS', '설정')}
                  </span>
                ),
                children: <p style={{ margin: 0 }}>{t('LBL_TAB_SETTINGS_BODY', '설정 탭.')}</p>
              },
              // 탭 3: BarChart 아이콘 + 통계
              {
                key: '3',
                label: (
                  <span>
                    <BarChartOutlined /> {t('LBL_DEMO_STATS', '통계')}
                  </span>
                ),
                children: <p style={{ margin: 0 }}>{t('LBL_TAB_STATS_BODY', '통계 탭.')}</p>
              }
            ]}
          />
        </Card>

        {/* ─── [Cell 3] Splitter — 좌(40%) / 우(60%) 가변 분할 ─
            드래그로 비율 조정 가능 (control + state binding 없이 단순 시연)

            【언제 Splitter?】 좌우(또는 상하) 두 패널의 비율을 사용자가 조정해야 할 때
                  - 마스터-디테일에서 디테일 영역을 넓히고 싶은 경우
                  - 트리 메뉴 너비를 사용자별로 다르게 가져갈 때
            【비교】 Grid/Flex 는 고정 비율, Splitter 만 런타임 드래그 가능
            【주의】 Panel 내부 콘텐츠가 minSize 보다 크면 자동 잘림 → overflow 처리 필요 */}
        <Card
          size="small"
          title="Splitter"
          style={{ height: '100%' }}
          styles={{ body: cardBodyStyle }}
        >
          {/* Splitter — height='100%' 로 카드 채움 + 보더로 패널 경계 시각화 */}
          <Splitter style={{ height: '100%', border: '1px solid #f0f0f0' }}>
            {/* 좌측 패널 — defaultSize='40%' (px 또는 % 모두 가능) */}
            <Splitter.Panel defaultSize="40%">
              <div style={{ padding: 10, fontSize: 12 }}>{t('LBL_LEFT', '왼쪽')} (40%)</div>
            </Splitter.Panel>
            {/* 우측 패널 — defaultSize 없음 → 잔여 공간(60%) 자동 차지 */}
            <Splitter.Panel>
              <div style={{ padding: 10, fontSize: 12 }}>{t('LBL_RIGHT', '오른쪽')} (60%)</div>
            </Splitter.Panel>
          </Splitter>
        </Card>

        {/* ─── [Cell 4] Steps — vertical 3-step + 이전/다음 제어 ─
            current 가 0/2 일 때 한쪽 버튼만 표시 (경계 처리)

            【왜 vertical?】 좁은 컬럼(1/4 폭)에 가로로 펴면 텍스트 잘림 → 세로 레이아웃이 가독성↑
            【언제 Steps?】 순차 진행이 강제되는 절차(가입/결재/주문) — 자유 이동 가능하면 Tabs
            【주의】 controlled (current + onChange) 시 검증 통과 후만 setCurrent 호출하지 않으면
                  사용자가 미완료 단계로 점프 가능 → 비즈니스 룰 누락 위험 */}
        <Card
          size="small"
          title="Steps"
          style={{ height: '100%' }}
          styles={{ body: cardBodyStyle }}
        >
          {/* current/onChange = controlled 모드 — Steps 헤드 클릭으로도 점프 가능
              items[i].title — 단계 라벨 (description, status 등도 옵션) */}
          <Steps
            size="small"
            direction="vertical"
            current={current}
            onChange={setCurrent}
            items={[
              { title: t('TTL_STEP_BASIC', '기본 정보') },
              { title: t('LBL_DETAIL_SETTINGS', '상세 설정') },
              { title: t('LBL_REVIEW_DONE', '검토 완료') }
            ]}
          />
          {/* 이전/다음 버튼 — 경계(0, 2)에서는 한쪽만 표시 (UX 명확성) */}
          <Space size={4} style={{ marginTop: 6 }}>
            {current > 0 && (
              // 0번째 단계가 아니면 '이전' 버튼 — default(회색) 스타일로 보조 액션 표현
              <Button size="small" onClick={() => setCurrent(current - 1)}>
                {t('BTN_PREV', '이전')}
              </Button>
            )}
            {current < 2 && (
              // 마지막(2)이 아니면 '다음' 버튼 — primary(파랑) 으로 주 액션 강조
              <Button size="small" type="primary" onClick={() => setCurrent(current + 1)}>
                {t('BTN_NEXT', '다음')}
              </Button>
            )}
          </Space>
        </Card>

        {/* ─── [Cell 5] Card 변형 ─
            1) type='inner' + extra(부제) — 카드 안의 카드
            2) cover (상단 이미지 영역) + 본문 — 미디어 카드

            【언제 type='inner'?】
              부모 Card 와 시각적 구분이 필요한 중첩 그룹 — 보더가 더 옅게 처리됨
              일반 Card 를 중첩하면 보더 강조 중복으로 시각적 혼잡
            【cover 패턴】 미디어 카드(상품/이미지 갤러리) — Card 상단에 이미지 영역을 분리
              cover 영역은 padding 없음 — 이미지/비디오를 Card 모서리까지 풀로 채움 */}
        <Card
          size="small"
          title={t('LBL_CARD_VARIANTS', 'Card 변형')}
          style={{ height: '100%' }}
          styles={{ body: cardBodyStyle }}
        >
          {/* Space direction='vertical' — 두 카드 세로 정렬 + 균일 gap */}
          <Space direction="vertical" size={6} style={{ width: '100%' }}>
            {/* 변형 1: type='inner' + extra(부제 슬롯)
                extra 는 카드 헤더 우측 — 메뉴/배지/링크 배치에 활용 */}
            <Card
              size="small"
              type="inner"
              title={t('LBL_BASIC_CARD', '기본 카드')}
              extra={
                <span style={{ fontSize: 11, color: '#999' }}>{t('LBL_SUBTITLE', '부제')}</span>
              }
            >
              <p style={{ margin: 0, fontSize: 12 }}>{t('LBL_CARD_BODY', '카드 내용')}</p>
            </Card>
            {/* 변형 2: cover — 카드 상단 미디어 영역 (실제로는 <img src=...> 가 일반적) */}
            <Card
              size="small"
              type="inner"
              title={t('LBL_IMAGE_CARD', '이미지 카드')}
              cover={
                <div
                  style={{
                    background: '#1677ff',
                    height: 40,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: 14
                  }}
                >
                  IMG
                </div>
              }
            >
              <p style={{ margin: 0, fontSize: 12 }}>{t('LBL_IMAGE_INCLUDED', '이미지 포함')}</p>
            </Card>
          </Space>
        </Card>

        {/* ─── [Cell 6] Toolbar + Divider + Anchor ─
            좌(등록/수정) / 우(엑셀/삭제) 액션 분리 + Divider 텍스트 + Anchor 링크

            【왜 좌/우 액션 분리?】
              좌측 = 주 액션(생성/편집), 우측 = 보조/위험 액션(삭제/내보내기)
              사용자 시선 흐름(좌→우) + 위험 액션을 의도적으로 손이 덜 닿는 우측에 배치
            【Divider 'OR'】 시각적 그룹 구분 — 두 그룹이 대등한 선택지일 때 (예: 로그인 vs 회원가입)
            【Anchor】 페이지 내 섹션 빠른 점프 — 긴 폼/문서에서 사이드 네비로 자주 사용
              affix=false: 스크롤 시 따라오지 않음 (sticky 가 필요하면 affix=true) */}
        <Card
          size="small"
          title="Toolbar / Divider"
          style={{ height: '100%' }}
          styles={{ body: cardBodyStyle }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <Space size={4}>
              <Button size="small" type="primary" icon={<PlusOutlined />}>
                {t('LBL_REGISTER', '등록')}
              </Button>
              <Button size="small" icon={<EditOutlined />}>
                {t('TTL_EDIT', '수정')}
              </Button>
            </Space>
            <Space size={4}>
              <Button size="small" icon={<DownloadOutlined />}>
                {t('BTN_EXCEL', '엑셀')}
              </Button>
              <Button size="small" danger icon={<DeleteOutlined />}>
                {t('BTN_DELETE', '삭제')}
              </Button>
            </Space>
          </div>
          <Divider style={{ margin: '6px 0' }}>OR</Divider>
          <Anchor
            affix={false}
            items={[
              { key: '1', href: '#panel-collapse', title: 'Collapse' },
              { key: '2', href: '#panel-splitter', title: 'Splitter' }
            ]}
          />
        </Card>

        {/* ─── [Cell 7] Typography ─
            1) Text 변형 (strong/italic/success/warning/danger/code/mark/under/strike) + Link
            2) Paragraph ellipsis (rows=2, expandable, 더보기 심볼)
            3) Paragraph copyable (클립보드 복사 버튼)

            【언제 Typography vs raw <p>/<span>?】
              - Typography: antd 테마 토큰(line-height, color, font-family) 자동 적용 → 일관성↑
              - raw HTML: 단발성 텍스트 / antd 와 무관한 영역에서만 사용
            【ellipsis 옵션 트레이드오프】
              expandable=true → 본문 전체를 DOM 에 보유 (검색 인덱싱 OK, 메모리↑)
              expandable=false → 잘린 텍스트만 DOM, 펼침 불가 (정적 표시용)
            【copyable】 내부에서 navigator.clipboard.writeText 호출 — HTTPS 컨텍스트 필요
              http:// 로컬 외 환경에서는 동작 실패 가능 */}
        <Card
          size="small"
          title="Typography"
          style={{ height: '100%' }}
          styles={{ body: cardBodyStyle }}
        >
          <Paragraph style={{ margin: 0, fontSize: 12 }}>
            <Text>Default.</Text> <Text strong>Strong.</Text> <Text italic>Italic.</Text>{' '}
            <Text type="success">Success.</Text> <Text type="warning">Warning.</Text>{' '}
            <Text type="danger">Danger.</Text> <Text code>code</Text> <Text mark>mark</Text>{' '}
            <Text underline>under</Text> <Text delete>strike</Text>{' '}
            <Link href="https://ant.design" target="_blank">
              Link
            </Link>
          </Paragraph>
          <Paragraph
            ellipsis={{ rows: 2, expandable: true, symbol: t('LBL_MORE', '더보기') }}
            style={{ fontSize: 12, margin: '6px 0' }}
          >
            {t(
              'LBL_DEMO_LONG_TEXT',
              '긴 문단은 ellipsis 로 줄여 표시할 수 있습니다. 확장 심볼을 클릭하면 전체 내용이 표시됩니다. 이 문단은 두 줄 이후로 잘려 표시됩니다.'
            )}
          </Paragraph>
          <Paragraph copyable style={{ fontSize: 12, margin: 0 }}>
            {t('LBL_COPYABLE', '복사 가능')}
          </Paragraph>
        </Card>

        {/* ─── [Cell 8] Watermark + 스크롤 영역 ─
            content 배열 → 반복 텍스트 워터마크 (보안/저작권 표시 패턴)
            내부 div: 고정 높이 + overflowY 로 스크롤 콘텐츠 데모

            【왜 Watermark?】 사용자 식별자(사번/이메일)를 화면에 깔아 스크린샷 유출 추적 용도
                  - 단순 보호이지 강력한 DRM 아님 (DOM 수정으로 제거 가능)
            【주의】 content 배열 길이가 길면 SVG 패턴 생성 비용↑ — 1~2개 짧은 문자열 권장
                  font.fontSize 키우면 패턴 간격(gap) 도 같이 조정해야 가독성 보존 */}
        <Card
          size="small"
          title="Watermark / ScrollPanel"
          style={{ height: '100%' }}
          styles={{ body: cardBodyStyle }}
        >
          {/* content: string[] — 워터마크 텍스트 (반복 패턴으로 깔림)
              font.fontSize=12 — 시각 방해 최소화 위한 작은 크기 */}
          <Watermark content={['monitoring-center']} font={{ fontSize: 12 }}>
            {/* 워터마크 자식 영역 — Watermark 가 SVG 패턴 background 로 자식 위에 깔음
                높이/배경/스크롤 명시 → 워터마크가 빈 영역에도 보이도록 */}
            <div
              style={{
                height: 90,
                padding: 8,
                background: '#fafafa',
                overflowY: 'auto',
                fontSize: 12
              }}
            >
              {/* Array.from({length:10}) — 10개 더미 라인으로 스크롤 시연
                  key={i} — index key 허용 (정적 리스트, 순서 변경 없음) */}
              {Array.from({ length: 10 }, (_, i) => (
                <p key={i} style={{ margin: '2px 0' }}>
                  {t('LBL_SCROLL_CONTENT', '스크롤 콘텐츠')} {i + 1}
                </p>
              ))}
            </div>
          </Watermark>
        </Card>
      </div>
    </PageContainer>
  )
}
